import { useCallback, useState } from 'react';
/**
 * Latches CONSENT_REQUIRED / PROFILE_INCOMPLETE off the server refusal (architect, 2026-09-30:
 * mount the re-ask the way `IdentityGapGate` works, never from a client-side consent guess).
 *
 * NOT sticky, unlike the identity gap: completing the step is exactly what makes the refusal
 * go away, so `resolve()` clears it. A later refusal (a second document, a changed version)
 * simply latches again.
 *
 * Only the two step-kinds latch. Everything else the classifier knows (stale version, withdraw
 * routing, step-up, ...) is the caller's inline concern and must not take over the screen.
 */
export function useConsentGap() {
    const [pending, setPending] = useState(null);
    const onApiError = useCallback((err) => {
        // The classifier + its copy table are ~5 kB in the shell's ENTRY chunk if imported statically, and
        // this handler runs on every API error of every session; a consent refusal is rare. Loaded on first
        // error instead (the entry-chunk budget on /agents, measured in the wiring MR).
        void import('./accountRefusal').then(({ classifyAccountRefusal }) => {
            const r = classifyAccountRefusal(err);
            if (r && (r.kind === 'consentRequired' || r.kind === 'profileIncomplete'))
                setPending(r);
        }).catch(() => {
            // A chunk-load failure must not become an unhandled rejection. The original refusal has already
            // reached the caller's own error path; the step simply does not latch this time and a retry re-asks.
        });
    }, []);
    const resolve = useCallback(() => setPending(null), []);
    return { pending, onApiError, resolve };
}
/**
 * `ApiProvider` takes ONE `onApiError`, so two latches (identity gap, consent gap) need an
 * explicit composition. Every handler runs even if an earlier one throws; the first error is
 * rethrown afterwards, so a broken latch is loud and cannot silence the other.
 */
export function composeApiErrorHandlers(...handlers) {
    return (err) => {
        let first;
        let failed = false;
        for (const h of handlers) {
            try {
                h(err);
            }
            catch (e) {
                if (!failed) {
                    failed = true;
                    first = e;
                }
            }
        }
        if (failed)
            throw first;
    };
}
//# sourceMappingURL=consentGap.js.map